/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import configuration from '../config';
import AuthenticatedComponent from '../utilities/AuthenticatedComponent';
import { createZipAndDownload } from '../utilities/CreateZipAndDownload';

import WithRouter from '../utilities/WithRouter';
import { setCurrentCampus, setCurrentGX } from '../actions';
import { formatBillPeriod, formatCommas } from '../utilities/HelperFunctions';
import { graphPieTotals, mapPosixToIANA } from '../utilities/SolarTrendsFunctionsHelper';
import { elexity } from '../utilities/SolarTrendsGraphHelper';
import moment from 'moment';
import 'moment-timezone';

import Select from 'react-select';
import ProcessingAndError from '../components/ProcessingAndError';
import CurrentWeather from '../components/CurrentWeather';
import SolarCampusTable from '../components/SolarCampusTable';
import SolarIcon from '../components/icons/SolarIcon';
import EnergyIcon from '../components/icons/EnergyIcon';
import PanelIcon from '../components/icons/PanelIcon';
import BatteryIcon from '../components/icons/BatteryIcon';
import icon_link from '../assets/icon_link.svg';
import { ReactComponent as ArrowIcon } from '../assets/icon_arrow.svg';
import { ReactComponent as BarIcon } from '../assets/icon_barChart.svg';
import { ReactComponent as PieIcon } from '../assets/icon_pieChart.svg';
import DownloadIcon from '../components/icons/DownloadIcon';

require('highcharts/modules/accessibility');
require('highcharts/modules/draggable-points');
require('highcharts/modules/exporting');
require('highcharts/modules/export-data');

const Solar = ({
  currentCampusId,
  currentCampusData,
  currentGXId,
  onGXChange,
  updateGXData,
  showMobile,
  processing,
  setProcessing,
  errorMessage,
  solarCampusData,
  checkForSolarData,
  router,
}) => {
  const authenticatedComponent = new AuthenticatedComponent();
  const errorMessageRef = useRef(null);
  const performanceRef = useRef(null);
  const selfConsumptionRef = useRef(null);
  const generationUseRef = useRef(null);

  const [showSolarDashboard, setShowSolarDashboard] = useState(false);
  const [singleSolarData, setSingleSolarData] = useState(null);
  const [billData, setBillData] = useState([]);
  const [gxOptions, setGXOptions] = useState([]);
  const [filesToZip, setFilesToZip] = useState([]);
  const [campusTimeZone, setCampusTimeZone] = useState(null);

  ////SELF CONSUMPTION PIE DATA////
  const [gridTotal, setGridTotal] = useState(0);
  const [solarConsumedTotal, setSolarConsumedTotal] = useState(0);
  const [solarExportedTotal, setSolarExportedTotal] = useState(0);

  ////GRAPH AND CONTROLS////
  const [activeTab, setActiveTab] = useState('performance');
  const [graphType, setChartType] = useState('bar');

  //PREV/NEXT range
  const [prevEnabled, setPrevEnabled] = useState(true);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [rangeCount, setRangeCount] = useState(0);

  //FREQUENCY daily/weekly/by bill
  const [activeFrequency, setActiveFrequency] = useState('BILL');

  //BILL PICKER
  const [billFromTime, setBillFromTime] = useState(null);
  const [billToTime, setBillToTime] = useState(null);

  const showBarPie = activeTab === 'selfConsumption';

  let stateObj;
  let gxParam = router.location.pathname.split('/')[3];

  function getTZAdjustedTimestamp(dateString, type) {
    if (!campusTimeZone || !dateString) {
      return null;
    }

    if (typeof dateString !== 'string' || dateString.trim() === '') {
      console.error('Invalid dateString passed to getTZAdjustedTimestamp:', dateString);
      return null;
    }

    let momentObj = moment.tz(dateString, campusTimeZone);

    if (!momentObj.isValid()) {
      console.error(`Moment failed to parse date '${dateString}' with TZ '${campusTimeZone}'.`);
      return null;
    }

    if (type === 'start') {
      return momentObj.startOf('day').valueOf();
    }

    if (type === 'end') {
      return momentObj.startOf('day').valueOf();
    }

    return momentObj.valueOf();
  }

  useEffect(() => {
    replaceUrl();
  }, [updateGXData]);

  useEffect(() => {
    checkForSolarData();
    setProcessing(true);
    updateGXData(currentCampusId);

    if (
      currentGXId &&
      performanceRef !== null &&
      selfConsumptionRef !== null &&
      generationUseRef !== null
    ) {
      authenticatedComponent.getBills(currentGXId, billCallback);
    }
  }, [currentGXId, currentCampusId]);

  useEffect(() => {
    if (billData.length > 0 && campusTimeZone && billFromTime === null) {
      const latestEndDate = billData[0].endDate;
      let defaultFromValue;

      if (billData.length >= 12) {
        setRangeCount(12);
        setStartIndex(12);
        defaultFromValue = {
          value: getTZAdjustedTimestamp(billData[11].startDate, 'start'),
          label: `${formatBillPeriod(billData[11].startDate)}`,
        };
      } else {
        setRangeCount(billData.length - 1);
        setStartIndex(billData.length - 1);
        defaultFromValue = {
          value: getTZAdjustedTimestamp(billData[billData.length - 1].startDate, 'start'),
          label: `${formatBillPeriod(billData[billData.length - 1].startDate)}`,
        };
      }

      setBillFromTime(defaultFromValue);

      setBillToTime({
        value: getTZAdjustedTimestamp(latestEndDate, 'end'),
        label: `${formatBillPeriod(billData[0].endDate)}`,
      });
    }
  }, [billData, campusTimeZone]);

  useEffect(() => {
    updateIndexRange();
  }, [billFromTime, billToTime, campusTimeZone]);

  useEffect(() => {
    setGXOptions([]);
    makeGXOptions();

    if (solarCampusData.length !== 0) {
      if (solarCampusData.length > 1 && currentGXId === null) {
        setShowSolarDashboard(false);
        onGXChange(solarCampusData[0].gridInterconnectEntityKeyToken);
      } else {
        let findGX = solarCampusData.find(
          (gx) => gx.gridInterconnectEntityKeyToken === currentGXId
        );
        setSingleSolarData(findGX);
        setActiveTab(findGX?.energySite === null ? 'selfConsumption' : 'performance');
        setShowSolarDashboard(false);

        const rawTimeZoneCode = findGX?.timezone;

        if (rawTimeZoneCode) {
          const ianaCode = mapPosixToIANA(rawTimeZoneCode);
          if (ianaCode && ianaCode !== campusTimeZone) {
            setCampusTimeZone(ianaCode);
          }
        }
      }
    }
  }, [solarCampusData]);

  useEffect(() => {
    if (billData.length > 0) {
      let tempZip = [];

      for (var i = 0; i < billData.length; i++) {
        tempZip.push(billData[i].pdfDownloadUrl);
      }

      setFilesToZip(tempZip);
    }
  }, [billData]);

  function replaceUrl() {
    if ((gxParam === undefined || gxParam !== currentGXId) && currentGXId !== null) {
      window.history.replaceState(stateObj, '', `/solar/${currentCampusId}/${currentGXId}`);
    }
  }

  const updateIndexRange = () => {
    if (billFromTime === null || billToTime === null || campusTimeZone === null) {
      return;
    }

    let newStartIndex = -1;
    let newEndIndex = -1;

    for (let i = 0; i < billData.length; i++) {
      const dataStartTime = getTZAdjustedTimestamp(billData[i].startDate, 'start');
      const dataEndTime = getTZAdjustedTimestamp(billData[i].endDate, 'end');

      if (dataStartTime === billFromTime.value) {
        newStartIndex = i;
      }

      if (dataEndTime === billToTime.value) {
        newEndIndex = i;
      }

      if (newStartIndex !== -1 && newEndIndex !== -1) {
        break;
      }
    }

    if (newStartIndex !== -1 && newEndIndex !== -1) {
      const newRangeCount = newStartIndex - newEndIndex + 1;

      setStartIndex(newStartIndex);
      setEndIndex(newEndIndex);
      setRangeCount(newRangeCount);
    }

    initializeSolarGraph(activeTab, graphType, [billFromTime, billToTime], activeFrequency);
    setProcessing(false);
  };

  function billCallback(data) {
    setBillFromTime(null);
    setBillToTime(null);

    if (data && data.energyBills.length > 0) {
      setBillData(data.energyBills);
    } else {
      setBillData([]);
    }
  }

  function pieCallback(data) {
    setGridTotal(formatCommas(Math.round(data.gridTotal), 'kWh'));
    setSolarConsumedTotal(formatCommas(Math.round(data.solarConsumedTotal), 'kWh'));
    setSolarExportedTotal(formatCommas(Math.round(data.solarExportedTotal), 'kWh'));
  }

  const billFromArray = () => {
    if (!campusTimeZone || billToTime === null) return [];

    let tempAllOptions = billData;
    let tempOptions = [];

    for (let i = 0; i < tempAllOptions.length; i++) {
      let fromTime = getTZAdjustedTimestamp(tempAllOptions[i].startDate, 'start');

      if (fromTime <= billToTime.value) {
        tempOptions.push({
          value: fromTime,
          label: `${formatBillPeriod(tempAllOptions[i].startDate)}`,
        });
      }
    }

    return tempOptions;
  };

  const billToArray = () => {
    if (!campusTimeZone || billFromTime === null) return [];

    let tempAllOptions = billData;
    let tempOptions = [];

    for (let i = 0; i < tempAllOptions.length; i++) {
      let fromTime = getTZAdjustedTimestamp(tempAllOptions[i].startDate, 'start');

      if (fromTime >= billFromTime.value) {
        tempOptions.push({
          value: getTZAdjustedTimestamp(tempAllOptions[i].endDate, 'end'),
          label: `${formatBillPeriod(tempAllOptions[i].endDate)}`,
        });
      }
    }

    return tempOptions;
  };

  function makeGXOptions() {
    if (solarCampusData) {
      setGXOptions(
        solarCampusData.map((gx, index) => (
          <option
            key={`gx-option-${index}`}
            value={gx.gridInterconnectEntityKeyToken}
            selected={gx.gridInterconnectEntityKeyToken === currentGXId}
          >
            {gx.shortGridInterconnectDisplayName}
          </option>
        ))
      );
    }
  }

  function initializeSolarGraph(name, graphType, dateRange, activeFrequency) {
    const focusFromMs = billFromTime.value;
    const focusToMs = billToTime.value;
    const encodedFrom = encodeURIComponent(focusFromMs);
    const encodedTo = encodeURIComponent(focusToMs);

    elexity.gx.solarTrends.initAuth(authenticatedComponent.generateAuthenticatedHeader());
    elexity.gx.solarTrends.init(
      currentGXId,
      configuration['backend_host'] +
        '/ahiapi/' +
        '{entityKey}/gridInterconnect/trendsSolarGraphs' +
        `?focusFrom=${encodedFrom}&focusTo=${encodedTo}`,
      `${name}Graph`,
      name,
      graphType,
      dateRange,
      activeFrequency,
      (solarData) => {
        graphPieTotals(solarData, billFromTime, billToTime, pieCallback);
      },
      `${singleSolarData.longCampusDisplayName} - ${singleSolarData.shortGridInterconnectDisplayName}`
    );
  }

  ////HANDLERS////
  function handleGXChange(id) {
    onGXChange(id);
    setShowSolarDashboard(false);
  }

  function handleActiveTab(name) {
    setActiveTab(name);
    setChartType('bar');
    initializeSolarGraph(name, graphType, [billFromTime, billToTime], activeFrequency);
  }

  function handleFrequencyChange(type) {
    setActiveFrequency((prevFrequency) => type);
    initializeSolarGraph(activeTab, graphType, [billFromTime, billToTime], type);
  }

  function handleChartTypeChange(e) {
    setChartType(e.target.value);
    initializeSolarGraph(activeTab, e.target.value, [billFromTime, billToTime], activeFrequency);
  }

  function handleSkipRange(type) {
    if (!campusTimeZone) return;

    let newFromBill;
    let newToBill;

    let lastBill = billData.length;
    let toCap = 0;
    let fromCap = lastBill - 1;

    if (type === 'prev') {
      newFromBill = billData[startIndex + rangeCount];
      newToBill = billData[endIndex + rangeCount];

      setPrevEnabled((prevState) => {
        return startIndex + rangeCount + rangeCount < fromCap;
      });
      setNextEnabled((prevState) => {
        return endIndex + rangeCount + rangeCount > toCap;
      });
    } else {
      newFromBill = billData[startIndex - rangeCount];
      newToBill = billData[endIndex - rangeCount];

      setPrevEnabled((prevState) => {
        return startIndex - rangeCount - rangeCount <= fromCap;
      });
      setNextEnabled((prevState) => {
        return endIndex - rangeCount - rangeCount >= toCap;
      });
    }

    setBillFromTime({
      value: getTZAdjustedTimestamp(newFromBill.startDate, 'start'),
      label: `${formatBillPeriod(newFromBill.startDate)}`,
    });

    setBillToTime({
      value: getTZAdjustedTimestamp(newToBill.endDate, 'end'),
      label: `${formatBillPeriod(newToBill.endDate)}`,
    });
  }

  const handleZipDownload = () => {
    setProcessing(true);
    const fileNameAppend = singleSolarData.shortGridInterconnectDisplayName;
    createZipAndDownload(currentGXId, fileNameAppend, filesToZip, setProcessing);
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      height: '28px',
      minHeight: '28px',
    }),
    valueContainer: (base) => ({
      ...base,
      height: '28px',
      padding: '0 8px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '28px',
    }),
  };

  return (
    <SolarSection className={showMobile ? 'mobile' : 'desktop'}>
      <div className={showMobile ? 'header-container mobile' : 'header-container'}>
        <div className="header">
          <SolarIcon
            fillColor="none"
            strokeColor={'#02222B'}
            strokeWidth={2.5}
            circleStrokeWidth={2.5}
            width="18px"
          />
          <h1>Solar</h1>
        </div>

        <CurrentWeather currentCampusData={currentCampusData}></CurrentWeather>
      </div>

      {showSolarDashboard && currentGXId === null ? (
        <SolarCampusTable solarCampusData={solarCampusData} handleGXChange={handleGXChange} />
      ) : (
        <>
          {singleSolarData !== null && singleSolarData !== undefined && (
            <section className="solar-section">
              <ProcessingAndError
                processing={processing}
                errorMessage={errorMessage}
                errorMessageRef={errorMessageRef}
              />

              <div className="header-section">
                <h3>{singleSolarData.shortGridInterconnectDisplayName}</h3>
                <div className="resource-link">
                  <a href="/resources#charts-data">Chart and Data Explanations</a>
                  <img
                    src={icon_link}
                    alt="icon indicating the link will navigate away from the page"
                  />
                </div>
              </div>

              {gxOptions.length > 1 && (
                <div className="gx-menu">
                  <select name="current-gx" onChange={(e) => onGXChange(e.target.value)}>
                    {gxOptions}
                  </select>
                </div>
              )}

              {singleSolarData?.gridInterconnectEntityKeyToken && (
                <div className="site-stats">
                  {singleSolarData.energySite !== null && (
                    <>
                      <div className="block">
                        <PanelIcon
                          fillColor={'none'}
                          strokeColor={'#047290'}
                          strokeWidth={2}
                          innerWidth={1.5}
                          width="30px"
                        />
                        <div className="stat-content">
                          <span className="stat-title">PV Panels</span>
                          <span className="stat-count">
                            Count: {singleSolarData.totalModuleCount}
                          </span>
                          <p className="stat-value">
                            {singleSolarData?.solarCapacitykWdc?.toFixed(2)} kWdc
                          </p>
                        </div>
                      </div>
                      <div className="block">
                        <EnergyIcon
                          backgroundFill={'#047290'}
                          fillColor={'#047290'}
                          strokeColor={'#047290'}
                          strokeWidth={2}
                          width="34px"
                        />
                        <div className="stat-content">
                          <span className="stat-title">Inverters</span>
                          <span className="stat-count">
                            Count: {singleSolarData.totalInverterCount}
                          </span>
                          <p className="stat-value">
                            {singleSolarData?.inverterCapacitykWac?.toFixed(2)} kWac
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {singleSolarData?.batteryEnergyCapacitykWh > 0 && (
                    <>
                      <div className="block">
                        <BatteryIcon
                          backgroundFill={'none'}
                          fillColor={'#047290'}
                          strokeColor={'#047290'}
                          strokeWidth={6}
                          width="18px"
                        />
                        <div className="stat-content">
                          <span className="stat-title">Battery power</span>
                          <p className="stat-value">
                            {singleSolarData?.batteryPowerCapacitykW?.toFixed(2)} kW
                          </p>
                        </div>
                      </div>
                      <div className="block">
                        <BatteryIcon
                          backgroundFill={'none'}
                          fillColor={'#047290'}
                          strokeColor={'#047290'}
                          strokeWidth={6}
                          width="18px"
                        />
                        <div className="stat-content">
                          <span className="stat-title">Battery</span>
                          <p className="stat-value">
                            {singleSolarData?.batteryEnergyCapacitykWh?.toFixed(2)} kWh
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="static-graph-container">
                {singleSolarData ? (
                  <>
                    <div className="chart-nav">
                      <div className="tabs">
                        {singleSolarData.energySite !== null && (
                          <div
                            className={
                              activeTab === 'performance' ? 'performance active' : 'performance'
                            }
                            onClick={() => handleActiveTab('performance')}
                          >
                            Performance
                          </div>
                        )}

                        <div
                          className={
                            activeTab === 'selfConsumption'
                              ? 'selfConsumption active'
                              : 'selfConsumption'
                          }
                          onClick={() => handleActiveTab('selfConsumption')}
                        >
                          Self-Consumption
                        </div>

                        <div
                          className={
                            activeTab === 'generationUse' ? 'generationUse active' : 'generationUse'
                          }
                          onClick={() => handleActiveTab('generationUse')}
                        >
                          Generation Use
                        </div>
                      </div>
                    </div>

                    <div className="active-chart-container">
                      <div className="chart-options">
                        <div className="controls-container">
                          <Select
                            className="no-arrow"
                            name="from-bill-picker"
                            value={billFromTime}
                            onChange={setBillFromTime}
                            options={billFromArray()}
                            styles={customStyles}
                          />
                          <p>to</p>
                          <Select
                            className="no-arrow"
                            name="to-bill-picker"
                            value={billToTime}
                            onChange={setBillToTime}
                            options={billToArray()}
                            styles={customStyles}
                          />
                        </div>

                        <div className="controls-container">
                          <div className="control-buttons">
                            <button
                              id="prev"
                              className="prev"
                              onClick={() => handleSkipRange('prev')}
                              disabled={!prevEnabled}
                            >
                              <ArrowIcon />
                              <p>Prev</p>
                            </button>
                            <button
                              id="next"
                              className="next"
                              onClick={() => handleSkipRange('next')}
                              disabled={!nextEnabled}
                            >
                              <p>Next</p>
                              <ArrowIcon />
                            </button>
                          </div>
                        </div>

                        {showBarPie && (
                          <div className="controls-container">
                            <div className="chart-select">
                              {graphType === 'bar' ? (
                                <BarIcon width={18} height={18} />
                              ) : (
                                <PieIcon width={18} height={18} />
                              )}

                              <select
                                className="chart-type"
                                onChange={(e) => handleChartTypeChange(e)}
                                value={graphType}
                              >
                                <option value="bar">Bar</option>
                                <option value="pie">Pie</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {graphType === 'bar' && (
                          <div className="controls-container">
                            <div className="control-buttons">
                              <button
                                id="daily"
                                className={
                                  activeFrequency === 'DAILY' ? 'frequency active' : 'frequency'
                                }
                                onClick={() => handleFrequencyChange('DAILY')}
                              >
                                Daily
                              </button>

                              <button
                                id="weekly"
                                className={
                                  activeFrequency === 'WEEKLY' ? 'frequency active' : 'frequency'
                                }
                                onClick={() => handleFrequencyChange('WEEKLY')}
                              >
                                Weekly
                              </button>

                              <button
                                id="bill"
                                className={
                                  activeFrequency === 'BILL' ? 'frequency active' : 'frequency'
                                }
                                onClick={() => handleFrequencyChange('BILL')}
                              >
                                By Bill
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {activeTab === 'performance' && (
                        <div className="graph-container">
                          <div
                            id="performanceGraph"
                            className={
                              activeTab === 'performance' ? 'staticGraph' : 'hidden staticGraph'
                            }
                            ref={performanceRef}
                          ></div>
                        </div>
                      )}

                      {activeTab === 'selfConsumption' && (
                        <div
                          className={
                            graphType === 'pie' ? 'graph-container pie active' : 'graph-container'
                          }
                        >
                          <div
                            id="selfConsumptionGraph"
                            className={
                              activeTab === 'selfConsumption' ? 'staticGraph' : 'hidden staticGraph'
                            }
                            ref={selfConsumptionRef}
                          ></div>

                          {graphType === 'pie' && activeTab === 'selfConsumption' && (
                            <>
                              <table>
                                <thead>
                                  <tr className="table-cell">
                                    <th>Type</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <tr className="table-cell">
                                    <td>Grid Imports</td>
                                    <td>{gridTotal}</td>
                                  </tr>
                                  <tr className="table-cell">
                                    <td>Solar Consumed</td>
                                    <td>{solarConsumedTotal}</td>
                                  </tr>
                                  <tr className="table-cell">
                                    <td>Solar Exported</td>
                                    <td>{solarExportedTotal}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </>
                          )}
                        </div>
                      )}

                      {activeTab === 'generationUse' && (
                        <div
                          id="generationUseGraph"
                          className={
                            activeTab === 'generationUse' ? 'staticGraph' : 'hidden staticGraph'
                          }
                          ref={generationUseRef}
                        ></div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="graph-empty-state">
                    <SolarIcon
                      fillColor="none"
                      strokeColor={'#02222B'}
                      strokeWidth={2.5}
                      circleStrokeWidth={2.5}
                      width="18px"
                    />
                    <p>There is no solar data for you at this time.</p>
                  </div>
                )}
              </div>

              <div className="bill-period-container">
                {billData ? (
                  <>
                    <div className="subheader">
                      <p className="subhead">Bill Periods</p>
                      <button onClick={() => handleZipDownload()}>
                        <DownloadIcon
                          backgroundFill="#047290"
                          strokeColor="#FFFFFF"
                          strokeWidth={2}
                          width="18px"
                        />
                        Download All
                      </button>
                    </div>

                    <div className="bill-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Start Period</th>
                            <th>End Period</th>
                            <th>Grid Imports</th>
                            <th>Solar Generation</th>
                            <th>Solar Exports</th>
                            <th>Self-Consumption</th>
                          </tr>
                        </thead>

                        <tbody>
                          {billData.length !== 0 &&
                            billData.map((item, index) => (
                              <tr key={`bill-${index}`}>
                                <td>{formatBillPeriod(item.startDate)}</td>
                                <td>{formatBillPeriod(item.endDate)}</td>
                                <td>{formatCommas(Math.round(item?.gridImportkWh), 'kWh')}</td>
                                <td>{formatCommas(Math.round(item?.solarGenerationkWh), 'kWh')}</td>
                                <td>{formatCommas(Math.round(item?.solarExportkWh), 'kWh')}</td>
                                <td>
                                  {item?.solarSelfConsumptionPercent !== 'NaN' &&
                                  item?.solarSelfConsumptionPercent !== null
                                    ? `${(item?.solarSelfConsumptionPercent * 100).toFixed(0)}%`
                                    : 'No Data'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="no-data">
                    To enable tracking of your utility bills please send a request to{' '}
                    <a href="mailto:support@elexity.io">support@elexity.io</a>.
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </SolarSection>
  );
};

const SolarSection = styled.section`
  height: 100vh;
  overflow: auto;

  h3 {
    font-size: 16px;
  }

  p {
    margin: 0;

    &.subhead {
      font-size: 18px;
      font-weight: 600;
    }
  }

  .solar-section {
    display: flex;
    flex-flow: column;
    row-gap: 24px;

    .header-section {
      align-items: center;
      display: flex;
      justify-content: space-between;

      h3 {
        font-size: 18px;
        margin: 0;
      }

      .resource-link {
        align-items: center;
        display: flex;
        gap: 4px;

        a {
          color: #047290;
          font-size: 14px;
          font-weight: 600;

          &:hover {
            filter: brightness(0.85);
          }
        }
      }
    }

    .gx-menu {
      select {
        padding: 10px 28px 10px 16px;
      }
    }

    .active-chart-container,
    .bill-period-container {
      background: white;
      box-shadow: 0px 2px 6px #02222b14;
      padding: 20px 24px 24px;
    }

    .bill-period-container {
      border-radius: 12px;
      margin-bottom: 24px;

      .subheader {
        align-items: center;
        display: flex;
        justify-content: space-between;
      }
    }

    .site-stats {
      display: flex;
      gap: 24px;

      .block {
        background: white;
        border-radius: 8px;
        box-shadow: 0px 2px 6px #02222b14;
        display: flex;
        gap: 12px;
        padding: 24px;
        width: -webkit-fill-available;

        .stat-content {
          display: flex;
          flex-flow: column;
          gap: 2px;
          justify-content: center;

          .stat-title {
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
          }

          .stat-count {
            color: #02222b75;
            font-size: 12px;
          }

          p.stat-value {
            font-size: 16px;
            font-weight: 600;
          }
        }
      }
    }

    .static-graph-container .graph-empty-state {
      align-items: center;
      display: flex;
      gap: 12px;
      justify-content: center;
      padding: 26px 24px 24px;
    }

    .active-chart-container {
      border-radius: 0 12px 12px 12px;
      display: flex;
      flex-direction: column;
      gap: 24px;

      .chart-options {
        display: flex;
        flex-flow: row wrap;
        gap: 24px;

        .controls-container {
          align-items: center;
          display: flex;
          gap: 6px;
          justify-content: center;

          input {
            background-size: 18px 18px;
            border: 1px solid #c2d0d1;
            color: #02222b;
            font-size: 14px;
            font-weight: 600;
            padding: 6px 10px 6px 40px;
            width: 140px;
          }

          select {
            border: none;
            box-shadow: unset;
            text-align: center;
          }

          .chart-select {
            align-items: center;
            background: white;
            border: 1px solid #c2d0d1;
            border-radius: 4px;
            display: flex;
            justify-content: space-between;
            padding: 4px 6px;

            select {
              background-color: transparent;
              margin-right: -10px;
              padding: 0 34px 0 6px;
            }
          }

          .no-arrow {
            & > div:first-of-type {
              border: 1px solid #c2d0d1;
            }

            svg {
              fill: #02222b;
              stroke: #02222b;
            }
          }

          .control-buttons {
            display: flex;
            gap: 6px;
          }

          button {
            align-items: center;
            background: white;
            border: 1px solid #c2d0d1;
            border-radius: 4px;
            color: #02222b75;
            cursor: pointer;
            display: flex;
            font-size: 14px;
            font-weight: 400;
            gap: 6px;
            height: 28px;
            justify-content: center;
            padding: 4px;
            transition: ease-in-out all 250ms;

            &.prev,
            &.next {
              color: #02222b;
              width: 72px;

              svg {
                height: 16px;
                width: 16px;
                path,
                line {
                  stroke: #02222b;
                }
              }

              &:disabled {
                cursor: unset;
                opacity: 0.5;
              }
            }

            &.prev svg {
              transform: rotate(180deg);
            }

            &.occupied-option {
              width: 150px;
            }

            &.frequency {
              width: 85px;
            }

            &.frequency.active,
            &.occupied-option.active {
              background: #f7931e50;
              border: 1px solid #f7931e;
              color: #02222b;
              font-weight: 600;
            }
          }
        }
      }

      .occupied-options {
        align-items: center;
        display: flex;
        flex-flow: row wrap;
        gap: 24px;

        .start-end-weekday,
        .start-end-weekend,
        div.time-container {
          align-items: center;
          display: flex;
          justify-content: center;
          flex-flow: unset;
          gap: 12px;
        }

        /*TODO: redesign time start/end component*/
        div.time-container {
          align-items: center;
          border: 1px solid #c2d0d150;
          border-radius: 8px;
          display: flex;
          gap: 6px;
          height: 30px;
          justify-content: center;
          padding: 3px 6px;
          width: unset;

          label {
            display: none;
          }

          .time-input-container {
            margin: 0;

            select {
              background: unset;
              border: none;
              font-size: 14px;
              padding: 5px 10px;
            }

            img {
              display: none;
            }
          }

          .start-time-container {
            align-items: center;
            display: flex;
            gap: 12px;

            &::after {
              content: 'to';
              color: #62797e;
            }
          }
        }
      }

      .staticGraph {
        height: 500px;
      }

      .graph-container.pie .staticGraph {
        height: 300px;
      }

      .hidden {
        display: none;
        height: 0;
      }

      .graph-container.pie {
        align-items: center;
        display: flex;
        justify-content: center;
      }

      .graph-container.pie table,
      .graph-container.pie div {
        width: 50%;
      }

      .graph-container.pie table {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;

        thead {
          border-bottom: 1px solid #c2d0d1;
        }

        tr.table-cell {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          width: 25vw;
        }
      }
    }

    .bill-table {
      max-height: 500px;
      overflow: hidden;
      overflow-y: scroll;

      table {
        background: white;
        border-collapse: collapse;
        border-radius: 12px;
        border-spacing: 0 12px;
        table-layout: fixed;
        width: 100%;

        th {
          border-bottom: 2px solid #c2d0d1;
          text-align: left;
        }

        tr {
          height: 46px;

          &:last-of-type td {
            border-bottom: none;
          }
        }

        thead {
          background: white;
          position: sticky;
          top: 0;
          z-index: 2;
        }

        thead,
        td:first-of-type {
          color: #02222b;
          font-size: 14px;
          font-weight: 500;
        }

        td {
          color: #02222b;
          font-size: 14px;
          font-weight: 500;

          button {
            align-items: center;
            background: white;
            border: none;
            border-radius: 8px;
            color: white;
            cursor: pointer;
            display: flex;
            font-family: 'Inter', Arial, Helvetica, sans-serif;
            font-size: 14px;
            font-weight: 600;

            img {
              transition: ease-in-out all 250ms;
            }

            &:hover img {
              opacity: 0.5;
              transition: ease-in-out all 250ms;
            }
          }
        }
      }
    }

    .no-data {
      border-radius: 8px;
      padding: 24px;
      text-align: center;
      a {
        background: none;
        color: #02222b;
        padding: 0;
        text-decoration: underline;
        transition: ease-in-out all 250ms;
        &:hover {
          background: none;
          color: #02be7f;
          padding: 0;
          transition: ease-in-out all 250ms;
        }
      }
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    currentCampusId: state.currentCampusId,
    currentGXId: state.currentGXId,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentCampus: (campusId) => {
      dispatch(setCurrentCampus(campusId));
    },
    setCurrentGX: (gxId) => {
      dispatch(setCurrentGX(gxId));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(Solar));
